import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import HiddenText from '../HiddenText';
import { useWeekLockVisibility } from '../../hooks/useWeekLockVisibility';
import {
  calculatePickMargin,
  formatLockLineDisplay,
  getMarginColorClass,
  getPickLabel,
} from '../../utils/gameDetailUtils';
import styles from './GameDetailModal.module.css';

const GameDetailLocks = ({
  game,
  gameLocks,
  displayedWeek,
  userId,
  previewMode = false,
}) => {
  const visibility = useWeekLockVisibility(displayedWeek);

  const locksWithMargins = useMemo(() => (
    gameLocks.map((lock) => ({
      ...lock,
      margin: calculatePickMargin(
        lock.pickType,
        lock.lockLine,
        game.home_score,
        game.away_score
      ),
    })).sort((a, b) => {
      const pickOrder = { away: 0, home: 1, over: 2, under: 3 };
      return pickOrder[a.pickType] - pickOrder[b.pickType];
    })
  ), [gameLocks, game.home_score, game.away_score]);

  const renderLockTile = useCallback((lock) => {
    const pickLabel = getPickLabel(lock.pickType, game);
    const line = formatLockLineDisplay(
      lock.lockLine,
      lock.pickType === 'over' || lock.pickType === 'under'
    );
    const tileClass = getLockTileClass(game, lock);
    const statusText = getLockStatusText(game, lock);
    const isUserLock = lock.userId === userId;
    const showUsername = previewMode || visibility.shouldShowUsername(isUserLock);
    const showPickInfo = previewMode || visibility.shouldShowPickInfo();
    const isNeutral = tileClass === styles.lockTileNeutral;

    return (
      <div
        key={`${lock.userId}-${lock.pickType}`}
        className={`${styles.lockTile} ${tileClass}`}
      >
        <span className={styles.lockTileUser}>
          {showUsername ? lock.username : (
            <HiddenText type="locker" weekNumber={displayedWeek} showTooltip />
          )}
        </span>
        <span className={styles.lockTilePick}>
          {showPickInfo ? (
            <>{pickLabel} {line}</>
          ) : (
            <HiddenText type="lock" weekNumber={displayedWeek} showTooltip />
          )}
        </span>
        <span className={`${styles.lockTileStatus} ${isNeutral ? styles.lockTileStatusNeutral : styles.statusBadge}`}>
          {statusText}
        </span>
      </div>
    );
  }, [game, displayedWeek, userId, previewMode, visibility]);

  if (
    locksWithMargins.length === 0
    || (!previewMode && !visibility.shouldShowLocksSection(locksWithMargins.length > 0))
  ) {
    return (
      <p className={styles.emptyState}>No league locks to display for this game.</p>
    );
  }

  return (
    <div className={styles.lockGrid}>
      {locksWithMargins.map(renderLockTile)}
    </div>
  );
};

const getLockTileClass = (game, lock) => {
  if (game.game_status === 'completed') {
    if (lock.won_lost === 'won') return styles.winning;
    if (lock.won_lost === 'lost') return styles.losing;
    return styles.lockTileNeutral;
  }
  if (game.game_status === 'in_progress') {
    return getMarginColorClass(lock.margin, styles) || styles.lockTileNeutral;
  }
  return styles.lockTileNeutral;
};

const getLockStatusText = (game, lock) => {
  if (game.game_status === 'completed') {
    return lock.won_lost
      ? lock.won_lost.charAt(0).toUpperCase() + lock.won_lost.slice(1)
      : 'Locked';
  }
  if (game.game_status === 'in_progress') {
    if (lock.margin > 0) return `W'ing +${lock.margin.toFixed(1)}`;
    if (lock.margin < 0) return `L'ing ${lock.margin.toFixed(1)}`;
    return 'Push';
  }
  return 'Locked';
};

GameDetailLocks.propTypes = {
  game: PropTypes.object.isRequired,
  gameLocks: PropTypes.array.isRequired,
  displayedWeek: PropTypes.number.isRequired,
  userId: PropTypes.number,
  previewMode: PropTypes.bool,
};

export default GameDetailLocks;
